import { Fragment } from "react";
import cx from "classnames";

import CopyFieldIcon from "components/CopyField/Icon";
import CardCollapsible from "ds/components/Card/Collapsible";
import TextEllipsis from "ds/components/TextEllipsis";
import Typography, { TypographyProps } from "ds/components/Typography";
import { useToggle } from "hooks/useToggle";
import { RunWorkerMetricsDiagnosticSeverity, RunWorkerMetricsStateError } from "types/generated";

import styles from "./styles.module.css";
import {
  getRunObservabilityErrorLines,
  getRunObservabilityErrorText,
  getRunObservabilityErrorTitle,
  RunObservabilityErrorPart,
} from "./utils";

const RUN_OBSERVABILITY_ERROR_PART_PROPS: Record<
  NonNullable<RunObservabilityErrorPart["style"]>,
  Pick<TypographyProps, "tag" | "variant" | "color" | "className">
> = {
  [RunWorkerMetricsDiagnosticSeverity.Error]: { tag: "span", variant: "s-t7", color: "danger" },
  [RunWorkerMetricsDiagnosticSeverity.Warning]: { tag: "span", variant: "s-t7", color: "warning" },
  strong: { tag: "span", variant: "s-t7" },
  highlight: { tag: "mark", className: styles.highlight },
  muted: { tag: "span", color: "secondary" },
};

type RunObservabilityErrorCardProps = {
  error: RunWorkerMetricsStateError;
  ariaLabel: string;
  initialIsCollapsed: boolean;
};

const RunObservabilityErrorCard = ({
  error,
  ariaLabel,
  initialIsCollapsed,
}: RunObservabilityErrorCardProps) => {
  const { resourceAddress } = error;
  const [isCollapsed, toggle] = useToggle(initialIsCollapsed);
  const lines = getRunObservabilityErrorLines(error);

  return (
    <CardCollapsible
      size="small"
      title={getRunObservabilityErrorTitle(resourceAddress)}
      titleSuffix={
        resourceAddress && (
          <TextEllipsis tooltip={resourceAddress}>
            {(ellipsisProps) => (
              <Typography
                {...ellipsisProps}
                tag="span"
                variant="p-body3"
                className={cx(ellipsisProps.className, styles.address)}
              >
                {resourceAddress}
              </Typography>
            )}
          </TextEllipsis>
        )
      }
      headerSuffix={
        <CopyFieldIcon title="Copy error" value={getRunObservabilityErrorText(lines)} />
      }
      ariaLevel={4}
      ariaLabel={ariaLabel}
      isCollapsed={isCollapsed}
      onToggle={toggle}
    >
      <Typography tag="pre" variant="s-body3" className={styles.message}>
        {lines.map((line, index) => (
          <Fragment key={index}>
            {index > 0 && "\n"}
            {line.map(({ text, style }, partIndex) =>
              style ? (
                <Typography key={partIndex} {...RUN_OBSERVABILITY_ERROR_PART_PROPS[style]}>
                  {text}
                </Typography>
              ) : (
                text
              )
            )}
          </Fragment>
        ))}
      </Typography>
    </CardCollapsible>
  );
};

export default RunObservabilityErrorCard;
